/////////////////////////////////////////////////////////////////////////////////////////////////////////
//  컴포넌트명 : ChatRoom (1:1 상담톡 채팅창)
//  설명 : 상담방 1개의 대화창(헤더 + 말풍선 목록 + 입력/첨부). X9003M1(1:1상담톡 탭)과 mainFrame의
//        플로팅 상담패널(ChatWidget)이 같은 코드를 쓰도록 분리한 공용 컴포넌트임.
//        - 마운트 시 최근 메시지를 불러오고(상담방마다 key로 새로 마운트), 이후 5초 폴링으로 새 메시지를 이어붙임
//          (화면이 안 보이면 중지 - 서버 rate limit 분당 300회 고려)
//        - 상대가 보낸 메시지를 화면에서 확인한 시점에 읽음처리(apiX9003M1Read)하고 onRead로 부모에 알림
//        - 대화이력은 삭제 없이 보관, 첨부파일은 인증된 다운로드 API(blob)로만 받음
//        - 승인제 : 상담방 상태(W 승인대기 / A 상담중 / C 종료)에 따라 입력창을 열고 닫음. 상태는 메시지 조회
//          응답(room)으로 함께 받아 폴링 때마다 갱신함(승인·종료를 바로 감지). 대화는 A(상담중)에서만 가능.
//        - 담당자는 승인대기(W) 방을 열어 요청 내용을 읽기 전용으로 확인한 뒤, 헤더의 [승인] 버튼을 눌러야 상담이 시작됨.
//  개발이력 : 2026.09.20  X9003M1.tsx에서 분리 / 승인제(상태별 입력 제어, 시스템메시지, 요청취소/상담종료) 추가
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config'; // API 기본 URL
import '../styles/chatDesk.css'; // 상담톡 전용 스타일
import { formatFileSize, getFileIcon } from '../utils/commonUtils'; // 첨부파일 아이콘/용량표시 공통 유틸
import { useMsgBoxModal } from './CommonModal'; // 공통 모달
import ChatPendingFiles from './ChatPendingFiles'; // 전송 대기 첨부파일 칩(이미지 썸네일)
import ImageLightbox from './ImageLightbox'; // 이미지 확대 미리보기
import { getPastedImageFiles } from '../utils/clipboardUtils'; // 클립보드 이미지 붙여넣기

export interface ChatFile { file_seq: number; msg_seq: number; org_file_nm: string; file_size: number; expire_dt?: string; }
export interface ChatMsg { msg_seq: number; sender_id: string; sender_nm: string; sender_type: string; content: string; insrt_dt: string; files: ChatFile[]; }
export interface ChatRoomInfo { room_id: number; user_id: string; user_nm: string; title?: string | null; status?: string; last_msg_txt?: string; last_msg_dt?: string; mgr_unread?: number; user_unread?: number; mgr_id?: string | null; mgr_nm?: string | null; closed_by?: string | null; }
// 상담방 현재 상태 - status: W 승인대기 / A 상담중 / C 종료, mgr_*: 승인(입장)한 담당자
// user_read_seq/mgr_read_seq : 사용자 쪽/담당자 쪽이 어디까지(메시지 번호) 읽었는지 - 상대가 내 메시지를 읽었는지("읽음" 표기) 판단용
export interface ChatRoomState { status: string; title?: string | null; mgr_id?: string | null; mgr_nm?: string | null; user_read_seq?: number | string | null; mgr_read_seq?: number | string | null; }
// 종료(C) 중 담당자가 없는(승인 전에 사용자가 요청을 취소한) 상담은 "취소", 담당자가 종료한 상담은 "종료"로 표시함
// (담당자는 승인해야 생기므로 C이면서 담당자(mgr_id)가 없으면 승인 전 취소임)
export const isChatCanceled = (st?: string, mgrId?: string | null) => st === 'C' && !mgrId;
export const chatStatusLabel = (st?: string, mgrId?: string | null) => (st === 'W' ? '승인대기' : st === 'C' ? (isChatCanceled(st, mgrId) ? '취소' : '종료') : '상담중');

export interface ChatRoomProps {
  room: ChatRoomInfo;
  myId: string;
  isManager: boolean;
  onBack?: () => void;                    // (담당자, 모바일) 상담방 목록으로 복귀
  onClose?: () => void;                   // (플로팅 패널) 패널 닫기
  onRead?: (roomId: number) => void;      // 읽음처리 완료 알림(목록 배지/플로팅 배지 갱신용)
  onRoomChange?: (state: ChatRoomState) => void; // 상담방 상태(승인/종료)/담당자가 바뀌었을 때 알림(목록 갱신, 종료 시 채팅창 리셋용)
  onNewRequest?: () => void;              // (사용자) 종료된 상담에서 "새 상담 요청" 눌렀을 때
  onSent?: () => void;                    // 메시지 전송 완료 알림(목록 최근메시지 갱신용)
  wide?: boolean;                         // (상담톡 화면) 넓게 보기 상태 - 헤더 우측 넓게/좁게 아이콘 표시용
  onToggleWide?: () => void;              // 넓게/좁게 토글(전달하지 않으면 아이콘을 표시하지 않음 - 미니패널)
}

const POLL_MS = 5000;           // 새 메시지 폴링 주기
const PAGE_LIMIT = 30;          // 한 번에 불러올 메시지 수(최초/이전이력 더보기)
const MAX_ATTACH_COUNT = 5;     // 메시지 1건당 첨부 최대 개수(서버 multer 제한과 동일)
const MAX_ATTACH_SIZE_MB = 20;  // 파일당 최대 용량(서버 제한과 동일)
const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp)$/i;

// DB(datetime)는 서버 로컬시각이 그대로 'Z'가 붙어 내려오므로(mssql 드라이버 특성) Date 변환 없이
// 문자열 그대로 잘라서 씀 - new Date()로 바꾸면 브라우저 시간대만큼 어긋남.
export const chatDayOf = (dt?: string) => (dt || '').slice(0, 10);
export const chatTimeOf = (dt?: string) => (dt || '').slice(11, 16);
const dayLabel = (dt?: string) => {
  const [y, m, d] = chatDayOf(dt).split('-');
  return y ? `${y}년 ${Number(m)}월 ${Number(d)}일` : '';
};

// 첨부파일 보관기간 - 업로드일+1개월(expire_dt, 날짜)까지 다운로드 가능, 다음날부터 불가(서버가 410으로 최종 차단).
// 화면 표시용 판정이며 이미 화면에 떠있는 메시지도 날짜가 바뀌면 자동으로 만료 표시가 됨.
const EXPIRED_MSG = '보관기간(1개월)이 만료되어 다운로드할 수 없습니다.';
const isFileExpired = (f: ChatFile) => {
  if (!f.expire_dt) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return chatDayOf(f.expire_dt) < today;
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 말풍선 안 이미지 미리보기 - 무인증 정적경로가 아니라 인증 API(blob)로 받아와서 표시
////////////////////////////////////////////////////////////////////////////////////////////////////////////
// onClick : 이미지를 아직 못 받았거나 만료된 경우(플레이스홀더)의 동작(다운로드 시도/만료 안내) / onPreview : 받은 이미지를 눌렀을 때 확대 미리보기
const ChatImage: React.FC<{ file: ChatFile; onClick: () => void; onPreview: (src: string) => void }> = ({ file, onClick, onPreview }) => {
  const [src, setSrc] = useState('');
  const expired = isFileExpired(file);
  useEffect(() => {
    if (expired) return; // 보관기간 만료 파일은 미리보기도 받지 않음(서버도 410으로 막음)
    let alive = true;
    let objUrl = '';
    axios.post(`${API_BASE_URL}/za/apiX9003M1FileDownload`, { data: { file_seq: file.file_seq } }, { responseType: 'blob' })
      .then(res => { if (alive) { objUrl = URL.createObjectURL(res.data); setSrc(objUrl); } })
      .catch(() => {});
    return () => { alive = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [file.file_seq, expired]);
  if (!src) return <div className={`chatDesk-file${expired ? ' expired' : ''}`} onClick={onClick}><i className="fas fa-file-image" /><span className="chatDesk-file-nm">{file.org_file_nm}</span></div>;
  return <img className="chatDesk-img" src={src} alt={file.org_file_nm} title="클릭하면 크게 봅니다" onClick={() => onPreview(src)} />;
};

const attachFiles = (list: ChatFile[], msgs: any[]): ChatMsg[] =>
  msgs.map(m => ({
    ...m,
    msg_seq: Number(m.msg_seq),
    files: list.filter(f => Number(f.msg_seq) === Number(m.msg_seq)).map(f => ({ ...f, msg_seq: Number(f.msg_seq), file_seq: Number(f.file_seq), file_size: Number(f.file_size) })),
  }));

////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 메인컴포넌트 : ChatRoom
////////////////////////////////////////////////////////////////////////////////////////////////////////////
const ChatRoom: React.FC<ChatRoomProps> = ({ room, myId, isManager, onBack, onClose, onRead, onRoomChange, onNewRequest, onSent, wide, onToggleWide }) => {
  const { showMsgBox, confirmMsgBox } = useMsgBoxModal();
  const [roomState, setRoomState] = useState<ChatRoomState>({ status: room.status || 'A', title: room.title, mgr_id: room.mgr_id, mgr_nm: room.mgr_nm, user_read_seq: 0, mgr_read_seq: 0 });
  const roomStateRef = useRef<ChatRoomState>(roomState); // 폴링 응답과 비교용(콜백 안에서 최신 상태 참조)
  const closingByMeRef = useRef(false); // 내가 종료/취소를 눌렀는지(상대가 종료했을 때만 "종료되었습니다" 안내를 띄우기 위함)
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [text, setText] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState<{ src: string; name: string } | null>(null); // 말풍선 이미지 확대 미리보기

  const rootRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastSeqRef = useRef(0);         // 마지막으로 받은 메시지 번호(폴링 기준)
  const pollBusyRef = useRef(false);
  const aliveRef = useRef(true);        // 언마운트 이후 늦게 도착한 응답 무시
  const scrollModeRef = useRef<'bottom' | 'keep' | 'none'>('none'); // 다음 렌더 후 스크롤 처리 방식
  const prevHeightRef = useRef(0);
  const roomId = room.room_id;

  const post = useCallback(async (api: string, data: Record<string, any>) => {
    const res = await axios.post(`${API_BASE_URL}/za/${api}`, { data });
    return res.data?.rsResults;
  }, []);

  // 다른 화면이 보이는 동안(탭 display:none, 브라우저 백그라운드)에는 폴링을 쉼.
  // position:fixed 패널은 offsetParent가 항상 null이라 getClientRects로 표시여부를 판단함.
  const isVisible = () => document.visibilityState === 'visible' && !!rootRef.current && rootRef.current.getClientRects().length > 0;

  // 메시지 목록 렌더 직후 스크롤 위치 처리 - 맨 아래로 / 이전이력 불러온 뒤 보던 위치 유지
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (scrollModeRef.current === 'bottom') el.scrollTop = el.scrollHeight;
    else if (scrollModeRef.current === 'keep') el.scrollTop = el.scrollHeight - prevHeightRef.current;
    scrollModeRef.current = 'none';
  }, [messages]);

  // 메시지 조회 응답의 room(현재 상태/제목/담당자)을 반영 - 바뀐 경우에만 갱신하고 부모에 알림
  const applyRoomState = useCallback((r?: ChatRoomState | null, freshMsgs?: ChatMsg[]) => {
    if (!r || !aliveRef.current) return;
    const prev = roomStateRef.current;
    const metaSame = prev.status === r.status && (prev.title ?? null) === (r.title ?? null) && (prev.mgr_id ?? null) === (r.mgr_id ?? null) && (prev.mgr_nm ?? null) === (r.mgr_nm ?? null);
    const readSame = Number(prev.user_read_seq || 0) === Number(r.user_read_seq || 0) && Number(prev.mgr_read_seq || 0) === Number(r.mgr_read_seq || 0);
    if (metaSame && readSame) return;
    const next = { status: r.status, title: r.title, mgr_id: r.mgr_id, mgr_nm: r.mgr_nm, user_read_seq: Number(r.user_read_seq || 0), mgr_read_seq: Number(r.mgr_read_seq || 0) };
    roomStateRef.current = next;
    setRoomState(next);
    if (metaSame) return; // 상대가 읽은 위치만 바뀐 경우 - 읽음 표기만 갱신하면 되고 부모 알림/안내는 필요 없음
    // 상대가 상담을 종료한 경우 안내(전역 모달이라 부모가 이 채팅창을 곧바로 리셋/언마운트해도 표시됨).
    // 사용자에게는 상담사가 남긴 종료인사(이번 응답의 마지막 상담사 메시지)를 안내창에 함께 보여줌 - 종료 즉시 채팅창이
    // 초기화되므로 대화창에서는 볼 수 없기 때문(대화 이력에는 그대로 남음).
    if (next.status === 'C' && prev.status !== 'C' && !closingByMeRef.current) {
      const greeting = isManager ? undefined : [...(freshMsgs || [])].reverse().find(m => m.sender_type === 'M')?.content;
      const canceledMsg = isChatCanceled(next.status, next.mgr_id) ? '상담 요청이 취소되었습니다.' : '';
      showMsgBox({ title: '알림', msgText: canceledMsg || (greeting ? `상담이 종료되었습니다.\n\n${greeting}` : '상담이 종료되었습니다.'), showBtn: 'OK', msgType: 'info' });
    }
    onRoomChange?.(next);
  }, [onRoomChange, showMsgBox, isManager]);

  // 읽음처리 - 상대가 보낸 메시지를 화면에서 확인한 시점에 안읽음 카운트 초기화
  const markRead = useCallback(async () => {
    try {
      await post('apiX9003M1Read', { room_id: roomId });
      if (aliveRef.current) onRead?.(roomId);
    } catch { /* 읽음처리 실패는 화면 동작에 영향 없음 - 다음 폴링에서 재시도됨 */ }
  }, [post, roomId, onRead]);

  // 새 메시지 가져오기(폴링/전송 직후 공용) - lastSeq 이후만 조회해서 이어붙임
  const fetchNew = useCallback(async () => {
    if (pollBusyRef.current) return;
    pollBusyRef.current = true;
    try {
      const rs = await post('apiX9003M1Msg', { room_id: roomId, after_seq: lastSeqRef.current, limit: 100 });
      if (!aliveRef.current || rs?.success === false) return;
      const fresh = attachFiles(rs?.files || [], rs?.messages || []);
      applyRoomState(rs?.room, fresh);
      if (fresh.length === 0) return;
      const el = listRef.current;
      const nearBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 120;
      const hasIncoming = fresh.some(m => m.sender_id !== myId);
      const hasMine = fresh.some(m => m.sender_id === myId);
      scrollModeRef.current = nearBottom || hasMine ? 'bottom' : 'none'; // 위로 스크롤해 이력을 보는 중이면 위치 유지
      lastSeqRef.current = fresh[fresh.length - 1].msg_seq;
      setMessages(prev => {
        const seen = new Set(prev.map(m => m.msg_seq));
        return [...prev, ...fresh.filter(m => !seen.has(m.msg_seq))];
      });
      if (hasIncoming) markRead();
    } catch { /* 일시적 네트워크 오류는 다음 주기에 재시도 */ }
    finally { pollBusyRef.current = false; }
  }, [post, roomId, myId, markRead, applyRoomState]);

  // 최초 진입 - 최근 PAGE_LIMIT건 로딩 + 읽음처리(상담방마다 이 컴포넌트가 key로 새로 마운트됨)
  useEffect(() => {
    aliveRef.current = true;
    (async () => {
      try {
        const rs = await post('apiX9003M1Msg', { room_id: roomId, limit: PAGE_LIMIT });
        if (!aliveRef.current) return;
        if (rs?.success === false) { showMsgBox({ title: '알림', msgText: rs.msgTxt, showBtn: 'OK', msgType: 'warning' }); return; }
        applyRoomState(rs?.room);
        const msgs = attachFiles(rs?.files || [], rs?.messages || []);
        scrollModeRef.current = 'bottom';
        setMessages(msgs);
        setHasMore((rs?.messages || []).length >= PAGE_LIMIT);
        lastSeqRef.current = msgs.length ? msgs[msgs.length - 1].msg_seq : 0;
        markRead();
      } catch {
        if (aliveRef.current) showMsgBox({ title: '알림', msgText: '대화 내용을 불러오지 못했습니다.', showBtn: 'OK', msgType: 'error' });
      }
    })();
    return () => { aliveRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // 폴링
  useEffect(() => {
    const timer = window.setInterval(() => { if (isVisible()) fetchNew(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [fetchNew]);

  // 이전 대화 더보기 - 현재 가장 오래된 메시지 이전 PAGE_LIMIT건을 위에 붙임
  const loadMore = useCallback(async () => {
    if (messages.length === 0) return;
    try {
      const rs = await post('apiX9003M1Msg', { room_id: roomId, before_seq: messages[0].msg_seq, limit: PAGE_LIMIT });
      if (!aliveRef.current || rs?.success === false) return;
      const older = attachFiles(rs?.files || [], rs?.messages || []);
      prevHeightRef.current = listRef.current?.scrollHeight || 0;
      scrollModeRef.current = 'keep';
      setMessages(prev => [...older, ...prev]);
      setHasMore((rs?.messages || []).length >= PAGE_LIMIT);
    } catch { /* 무시 - 사용자가 다시 누르면 재시도 */ }
  }, [roomId, messages, post]);

  // 첨부파일 선택
  // 첨부 대기 목록에 파일 추가(파일선택/클립보드 붙여넣기 공용) - 용량/개수 제한 검사, 같은 파일 중복 제거
  const addFiles = (picked: File[]) => {
    if (picked.length === 0) return;
    const tooBig = picked.find(f => f.size > MAX_ATTACH_SIZE_MB * 1024 * 1024);
    if (tooBig) {
      showMsgBox({ title: '알림', msgText: `[${tooBig.name}] 파일 용량이 너무 큽니다(파일당 최대 ${MAX_ATTACH_SIZE_MB}MB).`, showBtn: 'OK', msgType: 'warning' });
      return;
    }
    const merged = [...pendingFiles];
    picked.forEach(f => { const i = merged.findIndex(p => p.name === f.name && p.size === f.size); if (i < 0) merged.push(f); });
    if (merged.length > MAX_ATTACH_COUNT) {
      showMsgBox({ title: '알림', msgText: `한 번에 최대 ${MAX_ATTACH_COUNT}개까지 첨부할 수 있습니다.`, showBtn: 'OK', msgType: 'warning' });
    }
    setPendingFiles(merged.slice(0, MAX_ATTACH_COUNT));
  };
  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    addFiles(picked);
  };
  // 입력창에서 Ctrl+V - 클립보드에 텍스트 없이 이미지만 있으면(화면캡처 등) 첨부 대기 목록에 넣고, 텍스트가 있으면 기본 붙여넣기
  const onPasteInput = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const imgs = getPastedImageFiles(e);
    if (imgs.length === 0) return;
    e.preventDefault();
    addFiles(imgs);
  };

  // 전송 - 텍스트/파일 중 하나 이상 있어야 함. FormData는 Content-Type을 직접 지정하지 않음(boundary 누락 방지)
  const send = useCallback(async () => {
    if (sending) return;
    const content = text.trim();
    if (!content && pendingFiles.length === 0) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('room_id', String(roomId));
      formData.append('content', content);
      pendingFiles.forEach(f => formData.append('file', f));
      const res = await axios.post(`${API_BASE_URL}/za/apiX9003M1Send`, formData);
      const rs = res.data?.rsResults;
      if (!rs?.success) {
        showMsgBox({ title: '알림', msgText: rs?.msgTxt || '메시지를 전송하지 못했습니다.', showBtn: 'OK', msgType: 'warning' });
        return;
      }
      setText('');
      setPendingFiles([]);
      if (inputRef.current) inputRef.current.style.height = 'auto';
      await fetchNew();
      onSent?.();
    } catch (e: any) {
      showMsgBox({ title: '알림', msgText: e?.response?.data?.msgText || '메시지를 전송하지 못했습니다.', showBtn: 'OK', msgType: 'error' });
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [sending, text, pendingFiles, roomId, fetchNew, onSent, showMsgBox]);

  // 첨부파일 다운로드 - 인증 헤더가 필요해 링크 이동 대신 blob으로 받아서 저장
  const downloadFile = useCallback(async (f: ChatFile) => {
    if (isFileExpired(f)) {
      showMsgBox({ title: '알림', msgText: EXPIRED_MSG, showBtn: 'OK', msgType: 'warning' });
      return;
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/za/apiX9003M1FileDownload`, { data: { file_seq: f.file_seq } }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.org_file_nm;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      // 화면 판정과 서버(DB시각) 판정이 어긋난 경우(자정 근처 등)도 서버 410으로 안내
      showMsgBox({ title: '알림', msgText: e?.response?.status === 410 ? EXPIRED_MSG : '파일을 다운로드하지 못했습니다.', showBtn: 'OK', msgType: e?.response?.status === 410 ? 'warning' : 'error' });
    }
  }, [showMsgBox]);

  // 담당자: 요청 내용을 확인한 뒤 승인 - 승인해야 상담중(A)이 되어 대화가 열림. 여러 담당자가 동시에 눌러도 서버가
  // 먼저 처리된 한 명만 승인함(approved=false면 이미 다른 담당자가 승인한 것).
  const approveRoom = useCallback(async () => {
    const ok = await confirmMsgBox({ title: '상담 승인', msgText: '요청 내용을 확인하셨습니까? 승인하면 담당 상담사로 기록되고 상담(대화)이 시작됩니다.', showBtn: 'YesNo' });
    if (!ok) return;
    try {
      const rs = await post('apiX9003M1Approve', { room_id: roomId });
      if (!rs?.success) { showMsgBox({ title: '알림', msgText: rs?.msgTxt || '승인하지 못했습니다.', showBtn: 'OK', msgType: 'warning' }); return; }
      if (!rs.approved && rs.status !== 'W') showMsgBox({ title: '알림', msgText: '이미 다른 상담사가 승인했거나 종료된 상담입니다.', showBtn: 'OK', msgType: 'info' });
      await fetchNew(); // 상담중 상태/입장 안내를 바로 반영 -> 입력창이 열림
      onSent?.();
    } catch {
      showMsgBox({ title: '알림', msgText: '승인하지 못했습니다.', showBtn: 'OK', msgType: 'error' });
    }
  }, [confirmMsgBox, post, roomId, fetchNew, onSent, showMsgBox]);

  // 사용자: 승인대기 요청 취소 / 담당(승인)한 담당자: 상담 종료 - 서버(apiX9003M1Close)가 권한/상태를 최종 검증함
  const closeRoom = useCallback(async (kind: 'cancel' | 'end') => {
    const ok = await confirmMsgBox({
      title: kind === 'cancel' ? '요청 취소' : '상담 종료',
      msgText: kind === 'cancel' ? '상담 요청을 취소하시겠습니까?' : '상담을 종료하시겠습니까? 종료 후에는 대화할 수 없습니다.',
      showBtn: 'YesNo',
    });
    if (!ok) return;
    closingByMeRef.current = true;
    try {
      const rs = await post('apiX9003M1Close', { room_id: roomId });
      if (!rs?.success) { closingByMeRef.current = false; showMsgBox({ title: '알림', msgText: rs?.msgTxt || '처리하지 못했습니다.', showBtn: 'OK', msgType: 'warning' }); return; }
      await fetchNew(); // 종료 상태를 바로 반영 -> onRoomChange(C)로 부모가 채팅창을 리셋함
      onSent?.();
    } catch {
      closingByMeRef.current = false;
      showMsgBox({ title: '알림', msgText: '처리하지 못했습니다.', showBtn: 'OK', msgType: 'error' });
    }
  }, [confirmMsgBox, post, roomId, fetchNew, onSent, showMsgBox]);

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 한글 조합 중 Enter(조합 확정)는 전송으로 처리하면 마지막 글자가 중복 전송되므로 제외
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };
  const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  ////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 렌더링
  ////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 첨부파일 아래 만료일 안내 - 만료 전: "만료일 YYYY-MM-DD", 만료 후: "만료됨 (다운로드 불가)"
  const renderExpire = (f: ChatFile) => {
    if (!f.expire_dt) return null;
    return isFileExpired(f)
      ? <div className="chatDesk-file-exp expired">만료됨 (다운로드 불가)</div>
      : <div className="chatDesk-file-exp">만료일 {chatDayOf(f.expire_dt)}</div>;
  };

  const renderMessages = () => {
    const nodes: React.ReactNode[] = [];
    // 읽음 표기 - 메시지(말풍선)별로 표시함. 상대가 읽은 위치(담당자면 사용자 쪽, 사용자면 담당자 쪽 읽은 위치)까지의
    // 내 메시지마다 시각 위에 "읽음"을 붙임. 승인대기(W)에서는 표기하지 않음(서버도 승인 전에는 읽은 위치를 올리지
    // 않고, 승인과 동시에 읽음 처리됨).
    const counterpartReadSeq = Number((isManager ? roomState.user_read_seq : roomState.mgr_read_seq) || 0);
    const readReceiptOn = roomState.status !== 'W';
    messages.forEach((m, i) => {
      const prev = messages[i - 1];
      const mine = m.sender_id === myId;
      if (!prev || chatDayOf(prev.insrt_dt) !== chatDayOf(m.insrt_dt)) nodes.push(<div key={`d${m.msg_seq}`} className="chatDesk-date">{dayLabel(m.insrt_dt)}</div>);
      // 시스템 안내(입장/종료/취소) - 가운데 정렬 안내문
      if (m.sender_type === 'S') {
        nodes.push(<div key={m.msg_seq} className="chatDesk-system">{m.content}</div>);
        return;
      }
      // 같은 사람이 같은 분에 이어서 보낸 메시지는 이름/시각을 한 번만 표시
      const sameAsPrev = !!prev && prev.sender_type !== 'S' && prev.sender_id === m.sender_id && chatDayOf(prev.insrt_dt) === chatDayOf(m.insrt_dt);
      const next = messages[i + 1];
      const lastOfGroup = !next || next.sender_type === 'S' || next.sender_id !== m.sender_id || chatTimeOf(next.insrt_dt) !== chatTimeOf(m.insrt_dt) || chatDayOf(next.insrt_dt) !== chatDayOf(m.insrt_dt);
      const images = m.files.filter(f => IMAGE_EXT.test(f.org_file_nm));
      const others = m.files.filter(f => !IMAGE_EXT.test(f.org_file_nm));
      nodes.push(
        <div key={m.msg_seq} className={`chatDesk-row${mine ? ' me' : ''}${sameAsPrev ? '' : ' first'}`}>
          <div className="chatDesk-col">
            {/* 사용자 화면에서는 상담사 이름 대신 "상담사"로만 표시, 담당자 화면에서는 보낸 담당자 이름을 표시 */}
            {!mine && !sameAsPrev && <div className="chatDesk-sender">{m.sender_type === 'M' ? (isManager ? `${m.sender_nm || m.sender_id} (상담담당자)` : '상담사') : (m.sender_nm || m.sender_id)}</div>}
            <div className="chatDesk-bubble">
              {m.content}
              {images.length > 0 && (
                <div className="chatDesk-files" style={m.content ? undefined : { marginTop: 0 }}>
                  {images.map(f => (
                    <div key={f.file_seq} className="chatDesk-fitem">
                      <ChatImage file={f} onClick={() => downloadFile(f)} onPreview={src => setPreview({ src, name: f.org_file_nm })} />
                      {renderExpire(f)}
                    </div>
                  ))}
                </div>
              )}
              {others.length > 0 && (
                <div className="chatDesk-files" style={m.content || images.length ? undefined : { marginTop: 0 }}>
                  {others.map(f => {
                    const fi = getFileIcon(f.org_file_nm);
                    const expired = isFileExpired(f);
                    return (
                      <div key={f.file_seq} className="chatDesk-fitem">
                        <button type="button" className={`chatDesk-file${expired ? ' expired' : ''}`} onClick={() => downloadFile(f)} title={expired ? '보관기간 만료' : '다운로드'}>
                          <i className={`fas ${fi.icon}`} style={{ color: mine ? '#fff' : fi.color }} />
                          <span className="chatDesk-file-nm">{f.org_file_nm}</span>
                          <span className="chatDesk-file-sz">{formatFileSize(f.file_size)}</span>
                        </button>
                        {renderExpire(f)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {(lastOfGroup || (mine && readReceiptOn && m.msg_seq <= counterpartReadSeq)) && (
            <div className={`chatDesk-meta${mine ? ' me' : ''}`}>
              {mine && readReceiptOn && m.msg_seq <= counterpartReadSeq && <span className="chatDesk-read">읽음</span>}
              {lastOfGroup && <span className="chatDesk-time">{chatTimeOf(m.insrt_dt)}</span>}
            </div>
          )}
        </div>
      );
    });
    return nodes;
  };

  const partnerName = isManager ? `${room.user_nm || room.user_id} (${room.user_id})` : '';
  const st = roomState.status;
  const mgrLabel = roomState.mgr_id ? `${roomState.mgr_nm || roomState.mgr_id}${roomState.mgr_id === myId ? ' (나)' : ''}` : '';
  const canEnd = isManager && st === 'A' && roomState.mgr_id === myId; // 담당(승인)한 담당자만 종료 가능

  return (
    <section className="chatDesk-chat" ref={rootRef}>
      <div className="chatDesk-chat-head">
        {onBack && <button type="button" className="chatDesk-back" onClick={onBack}><i className="fas fa-chevron-left" /></button>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="title">
            {isManager ? partnerName : (roomState.title || '1:1 상담')}
            <span className={`chatDesk-status st-${st}${isChatCanceled(st, roomState.mgr_id) ? ' canceled' : ''}`}>{chatStatusLabel(st, roomState.mgr_id)}</span>
          </div>
          <div className="sub">
            {isManager
              ? <>{roomState.title ? `${roomState.title} · ` : ''}{mgrLabel ? <>담당자 <b>{mgrLabel}</b></> : (st === 'W' ? '승인 전' : '담당자 없음')}</>
              : (st === 'W' ? '상담사가 요청을 확인하면 상담이 시작됩니다.' : '')}{/* 사용자 화면에는 담당 상담사 표기 없음 */}
          </div>
        </div>
        {isManager && st === 'W' && <button type="button" className="chatDesk-head-btn primary" onClick={approveRoom}>승인</button>}
        {!isManager && st === 'W' && <button type="button" className="chatDesk-head-btn" onClick={() => closeRoom('cancel')}>요청 취소</button>}
        {canEnd && <button type="button" className="chatDesk-head-btn danger" onClick={() => closeRoom('end')}>상담 종료</button>}
        {onToggleWide && (
          <button type="button" className="chatDesk-close chatDesk-widen" onClick={onToggleWide} title={wide ? '좁게' : '넓게'}>
            <i className={`fas ${wide ? 'fa-compress' : 'fa-expand'}`} />
          </button>
        )}
        {onClose && <button type="button" className="chatDesk-close" onClick={onClose} title="닫기"><i className="fas fa-times" /></button>}
      </div>
      <div className="chatDesk-msgs" ref={listRef}>
        {hasMore && <button type="button" className="chatDesk-more" onClick={loadMore}>이전 대화 더보기</button>}
        {messages.length === 0 && (
          <div className="chatDesk-blank"><i className="far fa-comments" />아직 대화가 없습니다.</div>
        )}
        {renderMessages()}
      </div>
      {st === 'A' ? (
        <div className="chatDesk-composer">
          <ChatPendingFiles files={pendingFiles} onRemove={f => setPendingFiles(prev => prev.filter(p => p !== f))} />
          <div className="chatDesk-input-row">
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={onPickFiles} />
            <button type="button" className="chatDesk-attach" title="파일 첨부" onClick={() => fileInputRef.current?.click()}><i className="fas fa-paperclip" /></button>
            <textarea
              ref={inputRef}
              className="chatDesk-input"
              rows={1}
              value={text}
              maxLength={4000}
              placeholder="메시지를 입력하세요 (Enter 전송, Shift+Enter 줄바꿈, 이미지 붙여넣기 가능)"
              onChange={onInputChange}
              onKeyDown={onInputKeyDown}
              onPaste={onPasteInput}
            />
            <button type="button" className="chatDesk-send" disabled={sending || (!text.trim() && pendingFiles.length === 0)} onClick={send}>전송</button>
          </div>
          <div className="chatDesk-hint">파일은 최대 {MAX_ATTACH_COUNT}개, 파일당 {MAX_ATTACH_SIZE_MB}MB까지 첨부할 수 있으며 대화 내용은 모두 보관됩니다.</div>
        </div>
      ) : (
        <div className="chatDesk-composer chatDesk-locked">
          {st === 'W' ? (
            isManager
              ? <><i className="fas fa-hourglass-half" /> 요청 내용을 확인한 후 위의 [승인] 버튼을 누르면 상담이 시작됩니다.</>
              : <><i className="fas fa-hourglass-half" /> 상담사 승인 대기 중입니다. 담당 상담사가 요청을 확인하고 입장하면 대화할 수 있습니다.</>
          ) : (
            <>
              <i className="fas fa-lock" /> {isChatCanceled(st, roomState.mgr_id) ? '취소된 상담 요청입니다.' : '종료된 상담입니다.'} 대화 내용은 그대로 보관됩니다.
              {!isManager && onNewRequest && <button type="button" className="chatDesk-head-btn primary" onClick={onNewRequest}>새 상담 요청</button>}
            </>
          )}
        </div>
      )}
      {preview && <ImageLightbox src={preview.src} name={preview.name} onClose={() => setPreview(null)} />}
    </section>
  );
};

export default ChatRoom;
